/**
 * Pure helpers for the AI-reply feedback row (spec-traces-and-feedback sub-spec 05a).
 *
 * Chip keys and the free-text cap are READ from `configs/feedbackContract.js` (01a's mirror of
 * §4.0(a)); nothing here re-types a value that lives there.
 */
import { CHIP_KEYS_BY_RATING, TEXT } from "../configs/feedbackContract";

/** "other" — the last key of both chip lists, derived rather than retyped. */
export const OTHER_CHIP = CHIP_KEYS_BY_RATING.down.at(-1);

/** 500 — the ONLY spelling of the cap in the widget (§4.0(a): a constant, not configuration). */
export const FREE_TEXT_MAX = TEXT.FREE_TEXT_MAX;

/** The seven chip keys for a rating, `other` last. Anything that is not `up` is `down`. */
export const chipsFor = (rating) => (rating === "up" ? CHIP_KEYS_BY_RATING.up : CHIP_KEYS_BY_RATING.down);

/** The text a visitor actually sees, in TextMessage.jsx's precedence — Copy copies exactly this. */
export const replyText = (reply) => reply?._streamText ?? reply?.message ?? reply?.config?.message ?? "";

/** An AI reply is the only thing that can be rated (Receiver.jsx's AI/human discriminator). */
export const isAiReply = (reply) => !!reply?.agentIDaasId?.includes("ai_assistant");

/**
 * A server record id. A local echo carries `pending-<uuid>` and a record the socket has not
 * confirmed carries only a correlation id; the edge validates `recordId` as a Mongo id.
 */
export const isServerRecordId = (id) => typeof id === "string" && /^[a-f0-9]{24}$/i.test(id);

/**
 * Whether a record gets the feedback row: an AI reply with a server id, fully streamed, in a
 * known conversation. No feature flag (owner 2026-10-01): every such record gets the row.
 */
export const canRateRecord = (record, conversationId) =>
	!!record
	&& !record.pending
	&& isServerRecordId(record._id)
	&& !!conversationId
	&& isAiReply(record.reply)
	&& record.reply?.streaming !== true;

/** The free text as the field may hold it: never more than the cap. */
export const clampFreeText = (value) => String(value ?? "").slice(0, FREE_TEXT_MAX);

/**
 * Toggle one chip. De-selecting `other` DISCARDS the free text (FR-10a), so the caller learns
 * whether to clear the field from the same transition.
 * @returns {{ selected: string[], discardText: boolean }}
 */
export const toggleChip = (selected, key) => {
	const on = selected.includes(key);
	const next = on ? selected.filter((k) => k !== key) : [...selected, key];
	return { selected: next, discardText: on && key === OTHER_CHIP };
};

/**
 * The chips-and-note payload of a Send. `freeText` is carried ONLY while `other` is selected and
 * only when it has content — otherwise the edge would answer 400 FEEDBACK_TEXT_WITHOUT_OTHER.
 */
export const chipPayload = (selected, text) => {
	const chips = [...selected];
	const note = chips.includes(OTHER_CHIP) ? clampFreeText(text).trim() : "";
	return note ? { chips, freeText: note } : { chips };
};

/** How long the thumbs stay disabled after a 429 (the edge's window is 60 s per conversation). */
export const RATE_LIMIT_COOLDOWN_MS = 60 * 1000;
